import {
  FicChildOperation,
  FicChildrenBatchResponse,
  FicChildrenResponse,
} from 'src/services/wallet';
import { Operation } from 'src/types/operation';
import { Wallet, WalletFund } from 'src/types/wallet';

export type FicWalletRestriction = {
  parent: FicChildrenResponse['operation'];
  children: Operation[];
};

export type FicWalletRestrictionDiscovery = {
  restriction: FicWalletRestriction | null;
  ficParents: FicChildrenResponse[];
  hasDiscoveryError: boolean;
};

export type FundAdditionContext =
  | { type: 'global' }
  | { type: 'fic-child'; parentWalletFundId: number };

export function sameOperationHashId(
  left?: string | number | null,
  right?: string | number | null,
): boolean {
  const leftId = String(left ?? '').trim();
  const rightId = String(right ?? '').trim();

  return Boolean(leftId) && leftId === rightId;
}

type FicAdditionFund = {
  walletFundId?: number;
  hashId?: string | number | null;
};

type ResolveFicAdditionRestrictionParams = {
  context: FundAdditionContext | null;
  funds: ReadonlyArray<FicAdditionFund>;
  ficParents?: ReadonlyArray<FicChildrenResponse>;
  isLoading: boolean;
  hasDiscoveryError: boolean;
};

export type FicAdditionRestrictionState = {
  restrictedOperations?: Operation[];
  isLoading: boolean;
  isError: boolean;
  isSelectionBlocked: boolean;
  restrictionMessage?: string;
};

function isSelectableFicChildOperation(
  child: FicChildOperation | null | undefined,
): child is FicChildOperation {
  return (
    child !== null &&
    child !== undefined &&
    Number.isInteger(child.id) &&
    child.id > 0 &&
    typeof child.hashId === 'string' &&
    child.hashId.trim().length > 0 &&
    typeof child.name === 'string' &&
    child.name.trim().length > 0 &&
    typeof child.cnpj === 'string' &&
    child.cnpj.trim().length > 0 &&
    (child.slug == null || typeof child.slug === 'string')
  );
}

export function toFicChildOperations(
  children?: ReadonlyArray<FicChildOperation | null | undefined> | null,
): Operation[] {
  const ficChildren = Array.isArray(children) ? children : [];

  return ficChildren.reduce<Operation[]>((operations, child) => {
    if (!isSelectableFicChildOperation(child)) return operations;

    operations.push({
      id: child.id,
      hashId: child.hashId,
      name: child.name,
      cnpj: child.cnpj,
      slug: child.slug?.trim() || child.hashId,
      market: {
        id: 0,
        name: typeof child.market?.name === 'string' ? child.market.name : '',
        description: '',
      },
    });

    return operations;
  }, []);
}

/**
 * Restrict additions only when the user explicitly chose a FIC parent in the
 * wallet tree. A global add remains unrestricted even if the wallet contains
 * a FIC. A missing parent response is blocked so a FIC-child action fails
 * closed instead of falling back to the global fund catalogue.
 */
export function resolveFicAdditionRestriction({
  context,
  funds,
  ficParents = [],
  isLoading,
  hasDiscoveryError,
}: ResolveFicAdditionRestrictionParams): FicAdditionRestrictionState {
  if (context?.type !== 'fic-child') {
    return {
      isLoading: false,
      isError: false,
      isSelectionBlocked: false,
    };
  }

  if (isLoading) {
    return {
      isLoading: true,
      isError: false,
      isSelectionBlocked: true,
      restrictionMessage:
        'Você só pode adicionar FIDCs que fazem parte da composição deste FIC.',
    };
  }

  const parentFund = funds.find(
    (fund) => fund.walletFundId === context.parentWalletFundId,
  );
  const ficParent = ficParents.find((candidate) =>
    sameOperationHashId(candidate.operation.hashId, parentFund?.hashId),
  );
  const isError = hasDiscoveryError || !ficParent;

  if (isError) {
    return {
      isLoading: false,
      isError: true,
      isSelectionBlocked: true,
      restrictionMessage:
        'Você só pode adicionar FIDCs que fazem parte da composição deste FIC.',
    };
  }

  return {
    restrictedOperations: toFicChildOperations(ficParent.children),
    isLoading: false,
    isError: false,
    isSelectionBlocked: false,
    restrictionMessage:
      'Você só pode adicionar FIDCs que fazem parte da composição deste FIC.',
  };
}

export function getWalletOperationIds(walletFunds: WalletFund[]): Set<string> {
  const operationIds = new Set<string>();
  const visitedWalletFunds = new Set<number>();

  const collectWalletFund = (walletFund: WalletFund) => {
    if (visitedWalletFunds.has(walletFund.id)) return;

    visitedWalletFunds.add(walletFund.id);
    operationIds.add(walletFund.operation.hashId);

    if (walletFund.parentWalletFund) {
      operationIds.add(walletFund.parentWalletFund.operation.hashId);
    }

    walletFund.childrenWalletFunds?.forEach(collectWalletFund);
  };

  walletFunds.forEach(collectWalletFund);
  return operationIds;
}

/**
 * Discovery exists to refresh children of persisted FIC parents, never to
 * infer that an imported root is a FIC. Candidates are deduplicated and
 * sorted so the React Query key stays stable.
 */
export function getFicDiscoveryCandidates(walletFunds: WalletFund[]): string[] {
  const candidateOperationIds = new Set<string>();
  const visitedWalletFunds = new Set<number>();

  const collectCandidate = (walletFund: WalletFund) => {
    if (visitedWalletFunds.has(walletFund.id)) return;

    visitedWalletFunds.add(walletFund.id);

    if (
      walletFund.compositionRole === 'fic_parent' &&
      walletFund.operation?.hashId
    ) {
      candidateOperationIds.add(walletFund.operation.hashId);
    }

    if (
      walletFund.compositionRole === 'fic_child' &&
      walletFund.parentWalletFund?.operation?.hashId
    ) {
      candidateOperationIds.add(walletFund.parentWalletFund.operation.hashId);
    }

    walletFund.childrenWalletFunds?.forEach(collectCandidate);
  };

  walletFunds.forEach(collectCandidate);

  return Array.from(candidateOperationIds).sort();
}

export async function discoverFicWalletRestriction(
  wallet: Wallet,
  getFicChildrenBatch: (
    operationIds: string[],
  ) => Promise<FicChildrenBatchResponse>,
): Promise<FicWalletRestrictionDiscovery> {
  const candidateOperationIds = getFicDiscoveryCandidates(wallet.walletFunds);

  if (candidateOperationIds.length === 0) {
    return { restriction: null, ficParents: [], hasDiscoveryError: false };
  }

  try {
    const batch = await getFicChildrenBatch(candidateOperationIds);
    const ficParents: FicChildrenResponse[] = [];

    for (const result of batch.results) {
      if (result.status !== 'fic' || !result.operation) continue;

      ficParents.push({
        operation: result.operation,
        isFic: true,
        children: result.children ?? [],
      });
    }

    return {
      restriction: getFicWalletRestriction(wallet, ficParents),
      ficParents,
      hasDiscoveryError: false,
    };
  } catch {
    return { restriction: null, ficParents: [], hasDiscoveryError: true };
  }
}

export function getFicWalletRestriction(
  wallet: Wallet,
  ficResponses: FicChildrenResponse[],
): FicWalletRestriction | null {
  const walletOperationIds = getWalletOperationIds(wallet.walletFunds);

  for (const response of ficResponses) {
    if (!response.isFic || !walletOperationIds.has(response.operation.hashId)) {
      continue;
    }

    const children = toFicChildOperations(response.children);
    const childOperationIds = new Set(children.map((child) => child.hashId));
    const onlyFicOperations = Array.from(walletOperationIds).every(
      (operationId) =>
        operationId === response.operation.hashId ||
        childOperationIds.has(operationId),
    );

    if (!onlyFicOperations) continue;

    return {
      parent: response.operation,
      children,
    };
  }

  return null;
}
